/**
 * Token Registry
 * 
 * Enforces unique parameter groups in the scene state.
 * Each group allows only ONE active value. When duplicates
 * are detected, the registry can resolve them deterministically.
 */

export type TokenGroup =
  | 'camera_lens'
  | 'camera_perspective'
  | 'camera_shot_type'
  | 'camera_body'
  | 'camera_sensor'
  | 'aspect_ratio'
  | 'render_style'
  | 'color_temperature'
  | 'light_style'
  | 'light_preset'
  | 'film_stock'
  | 'lut_preset'
  | 'beauty_preset'
  | 'composition_primary'
  | 'time_of_day'
  | 'weather'
  | 'dof_mode';

export interface Token {
  group: TokenGroup;
  value: string;
  source: 'element' | 'global' | 'scene' | 'style';
  /** Element ID if source is 'element' */
  elementId?: string;
  /** Property key on the element or globalSettings */
  propertyKey?: string;
  /** Priority: higher wins when deduplicating. User-set > inferred > default */
  priority: number;
}

export interface TokenConflict {
  group: TokenGroup;
  tokens: Token[];
  winner: Token;
  losers: Token[];
}

export interface ConstraintLock {
  group: TokenGroup;
  lockedValue: string;
  lockedAt: number;
  reason: string;
}

// ─── Registry State ───

const constraintLocks = new Map<TokenGroup, ConstraintLock>();

export const lockConstraint = (group: TokenGroup, value: string, reason: string): void => {
  constraintLocks.set(group, {
    group,
    lockedValue: value,
    lockedAt: Date.now(),
    reason,
  });
};

export const unlockConstraint = (group: TokenGroup): void => {
  constraintLocks.delete(group);
};

export const getConstraintLock = (group: TokenGroup): ConstraintLock | null => {
  return constraintLocks.get(group) ?? null;
};

export const isGroupLocked = (group: TokenGroup): boolean => {
  return constraintLocks.has(group);
};

export const clearAllConstraints = (): void => {
  constraintLocks.clear();
};

export const getAllConstraints = (): ConstraintLock[] => {
  return [...constraintLocks.values()];
};

// ─── Token Extraction ───

import type { CanvasElement, GlobalSettings } from '@/lib/engineTypes';

/**
 * Extract all tokens from the current scene state.
 * Each canvas element and global setting is scanned for
 * values that belong to a unique token group.
 */
export const extractTokens = (
  elements: CanvasElement[],
  globalSettings: GlobalSettings,
  aspectRatio: string,
  stylePreset: string,
): Token[] => {
  const tokens: Token[] = [];

  // ── From elements ──
  elements.forEach((el) => {
    if (el.type === 'camera') {
      if (el.properties.lens) {
        tokens.push({ group: 'camera_lens', value: el.properties.lens, source: 'element', elementId: el.id, propertyKey: 'lens', priority: 10 });
      }
      if (el.properties.perspective) {
        tokens.push({ group: 'camera_perspective', value: el.properties.perspective, source: 'element', elementId: el.id, propertyKey: 'perspective', priority: 10 });
      }
      if (el.properties.shotType) {
        tokens.push({ group: 'camera_shot_type', value: el.properties.shotType, source: 'element', elementId: el.id, propertyKey: 'shotType', priority: 10 });
      }
      if (el.properties.cameraType) {
        tokens.push({ group: 'camera_body', value: el.properties.cameraType, source: 'element', elementId: el.id, propertyKey: 'cameraType', priority: 10 });
      }
      if (el.properties.sensorSize) {
        tokens.push({ group: 'camera_sensor', value: el.properties.sensorSize, source: 'element', elementId: el.id, propertyKey: 'sensorSize', priority: 10 });
      }
      if (el.properties.dof) {
        tokens.push({ group: 'dof_mode', value: el.properties.dof, source: 'element', elementId: el.id, propertyKey: 'dof', priority: 10 });
      }
    }

    if (el.type === 'light') {
      if (el.properties.lightStyle) {
        tokens.push({ group: 'light_style', value: el.properties.lightStyle, source: 'element', elementId: el.id, propertyKey: 'lightStyle', priority: 10 });
      }
      if (el.properties.lightPreset) {
        tokens.push({ group: 'light_preset', value: el.properties.lightPreset, source: 'element', elementId: el.id, propertyKey: 'lightPreset', priority: 10 });
      }
      if (el.properties.colorTempValue) {
        tokens.push({ group: 'color_temperature', value: el.properties.colorTempValue, source: 'element', elementId: el.id, propertyKey: 'colorTempValue', priority: 10 });
      }
    }
  });

  // ── From globalSettings ──
  if (globalSettings.renderStyle) {
    tokens.push({ group: 'render_style', value: globalSettings.renderStyle, source: 'global', propertyKey: 'renderStyle', priority: 8 });
  }
  if (globalSettings.filmStock && globalSettings.filmStock !== 'none') {
    tokens.push({ group: 'film_stock', value: globalSettings.filmStock, source: 'global', propertyKey: 'filmStock', priority: 8 });
  }
  if (globalSettings.lutPreset && globalSettings.lutPreset !== 'none') {
    tokens.push({ group: 'lut_preset', value: globalSettings.lutPreset, source: 'global', propertyKey: 'lutPreset', priority: 8 });
  }
  if (globalSettings.beautyPreset && globalSettings.beautyPreset !== 'none') {
    tokens.push({ group: 'beauty_preset', value: globalSettings.beautyPreset, source: 'global', propertyKey: 'beautyPreset', priority: 8 });
  }

  // ── Aspect ratio ──
  tokens.push({ group: 'aspect_ratio', value: aspectRatio, source: 'global', propertyKey: 'aspectRatio', priority: 10 });

  // ── Style ──
  if (stylePreset && stylePreset !== 'none') {
    tokens.push({ group: 'render_style', value: stylePreset, source: 'style', propertyKey: 'stylePreset', priority: 6 });
  }

  // ── Composition (mutual exclusion: rule of thirds vs centered vs golden ratio) ──
  const compValues: string[] = [];
  if (globalSettings.ruleOfThirds) compValues.push('rule_of_thirds');
  if (globalSettings.centeredCinematic) compValues.push('centered');
  if (globalSettings.goldenRatio) compValues.push('golden_ratio');
  if (globalSettings.symmetryMode) compValues.push('symmetry');
  compValues.forEach((v) => {
    tokens.push({ group: 'composition_primary', value: v, source: 'global', propertyKey: v, priority: 8 });
  });

  return tokens;
};

// ─── Normalization ───

const NORMALIZATION_MAP: Record<string, string> = {
  'eye level': 'eye_level',
  'eye-level': 'eye_level',
  'eye level perspective': 'eye_level',
  'eye level natural perspective': 'eye_level',
  'low angle': 'low_angle',
  'low-angle': 'low_angle',
  'high angle': 'high_angle',
  'high-angle': 'high_angle',
  'birds eye': 'birds_eye',
  "bird's eye": 'birds_eye',
  'birds-eye': 'birds_eye',
  'top down': 'birds_eye',
  'top-down': 'birds_eye',
  'close up': 'close_up',
  'close-up': 'close_up',
  'closeup': 'close_up',
  'medium shot': 'medium',
  'wide shot': 'wide',
  'full frame': 'full_frame',
  'full-frame': 'full_frame',
  'aps-c': 'apsc',
  'aps c': 'apsc',
  'rule of thirds': 'rule_of_thirds',
  'rule-of-thirds': 'rule_of_thirds',
  'golden ratio': 'golden_ratio',
  'golden-ratio': 'golden_ratio',
  'centered composition': 'centered',
  'center composition': 'centered',
};

export const normalizeTokenValue = (value: string): string => {
  const lower = value.trim().toLowerCase();
  return NORMALIZATION_MAP[lower] ?? lower;
};

/**
 * Detect duplicate tokens within the same group.
 * Returns a list of conflicts with a deterministic winner (highest priority, first added).
 */
export const detectTokenConflicts = (tokens: Token[]): TokenConflict[] => {
  const groups = new Map<TokenGroup, Token[]>();
  tokens.forEach((t) => {
    const list = groups.get(t.group) || [];
    list.push(t);
    groups.set(t.group, list);
  });

  const conflicts: TokenConflict[] = [];
  groups.forEach((groupTokens, group) => {
    // Deduplicate by NORMALIZED value — same normalized value is not a conflict
    const uniqueValues = new Map<string, Token>();
    groupTokens.forEach((t) => {
      const key = normalizeTokenValue(t.value);
      if (!uniqueValues.has(key) || t.priority > (uniqueValues.get(key)!.priority)) {
        uniqueValues.set(key, t);
      }
    });

    const distinct = [...uniqueValues.values()];
    if (distinct.length <= 1) return;

    // Check constraint lock
    const lock = getConstraintLock(group);
    let winner: Token;
    if (lock) {
      winner = distinct.find((t) => normalizeTokenValue(t.value) === normalizeTokenValue(lock.lockedValue)) || distinct.sort((a, b) => b.priority - a.priority)[0];
    } else {
      winner = distinct.sort((a, b) => b.priority - a.priority)[0];
    }

    const losers = distinct.filter((t) => t !== winner);
    conflicts.push({ group, tokens: distinct, winner, losers });
  });

  return conflicts;
};
